import type { Getter, PrimitiveAtom, Setter } from 'jotai';
import { isAbortLikeError } from '@studio/helpers/isAbortLikeError';
import type { CachedFetchLoadInFlight, StoreData } from '@studio/helpers/tsUtils';

type _RunCachedAbortableStoreFetchArgs<TData> = {
  dataAtom: PrimitiveAtom<StoreData<TData>>;
  emptyOnError: TData;
  fetchWithSignal: (signal: AbortSignal) => Promise<TData>;
  forceRefresh?: boolean;
  forceSilentRefresh?: boolean;
  loadInFlightAtom: PrimitiveAtom<CachedFetchLoadInFlight | null>;
  onSuccess?: (data: TData) => void;
  staleTimeoutMs: number;
};

function runCachedAbortableStoreFetch<TData>(
  get: Getter,
  set: Setter,
  args: _RunCachedAbortableStoreFetchArgs<TData>
): Promise<void> | undefined {
  const {
    dataAtom,
    emptyOnError,
    fetchWithSignal,
    forceRefresh,
    forceSilentRefresh,
    loadInFlightAtom,
    onSuccess,
    staleTimeoutMs,
  } = args;

  const forceFetch = Boolean(forceRefresh || forceSilentRefresh);
  const fromStore = get(dataAtom);
  const existing = get(loadInFlightAtom);
  if (existing) {
    const supersedeWithForce = Boolean(forceFetch && !existing.isForced);
    if (!supersedeWithForce) {
      return existing.promise;
    }
    existing.controller.abort();
  }
  if (!forceFetch && fromStore.timestamp && Date.now() - fromStore.timestamp < staleTimeoutMs) {
    return;
  }
  if (forceRefresh) {
    set(dataAtom, {
      data: emptyOnError,
      error: undefined,
      hasFetched: false,
      timestamp: 0,
    });
  } else if (forceSilentRefresh) {
    set(dataAtom, (prev) => ({
      ...prev,
      error: undefined,
      timestamp: Date.now(),
    }));
  }
  const controller = new AbortController();
  const isForced = forceFetch;
  const run = async () => {
    try {
      const res = await fetchWithSignal(controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      set(dataAtom, { data: res, hasFetched: true, timestamp: Date.now(), error: undefined });
      onSuccess?.(res);
    } catch (error) {
      if (isAbortLikeError(error)) {
        return;
      }
      console.error(error);
      set(dataAtom, { data: emptyOnError, hasFetched: true, timestamp: 0, error });
    }
  };
  const currentPromise = run().finally(() => {
    const cur = get(loadInFlightAtom);
    if (cur?.promise === currentPromise) {
      set(loadInFlightAtom, null);
    }
  });
  set(loadInFlightAtom, { controller, isForced, promise: currentPromise });
  return currentPromise;
}
export default runCachedAbortableStoreFetch;
